import keys from '@debbie/common/dist/cache/keys.js';
import { useMutation } from '@debbie/common/dist/util/hooks.js';
import * as updatesLib from '@debbie/cortex/dist/api/updates.js';
import { useIsAdmin } from '@debbie/cortex/dist/me/index.js';
import q from '@debbie/cortex/dist/query.js';
import { ensureUpdateTypesLoaded, updateTypesEntity, useUpdateTypes, } from '@debbie/cortex/dist/update-types/index.js';
import React, { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { errorNotification } from '../utils/errors.js';
import { Info, Pause, PlusCircle, Zap } from './Icons/index.js';
import { PALETTE, storeRecent } from './SearchableDropdown.js';
import SearchableSelect from './SearchableSelect.js';
const kindOf = (updateType) => updateType.procedureEnabled ? 'task' : updateType.pausing ? 'pause' : 'info';
const KIND_ICON = {
    task: { icon: React.createElement(Zap, null), palette: PALETTE.accent },
    pause: { icon: React.createElement(Pause, null), palette: PALETTE.warning },
    info: { icon: React.createElement(Info, null), palette: PALETTE.neutral },
};
const RECENTS_STORAGE_KEY = 'debbie.updateTypeSelect.recentlyUsed';
const updateTypeId = (updateType) => updateType.updateTypeId;
const kindIcon = (updateType) => KIND_ICON[kindOf(updateType)];
const updateTypeDescription = (updateType) => updateType.description;
/**
 * Searchable select for update types, narrowable by kind. Admins get a
 * create option while searching; the created type is selected immediately.
 */
const UpdateTypeSelect = (props) => {
    const lang = useSelector((s) => s.lang);
    const isAdmin = useIsAdmin();
    const updateTypes = useUpdateTypes();
    const createMutation = useMutation(async (label) => {
        const newUpdateType = await updatesLib.createType({
            label,
            ...props.createDetails,
        });
        q.invalidate(keys.write.create(updateTypesEntity));
        // onChange consumers (e.g. the workflow editor) look the new id up in
        // the update-types collection immediately, so wait for the reload
        await ensureUpdateTypesLoaded();
        const reloaded = await updatesLib.types();
        return (reloaded.find((updateType) => updateType.updateTypeId === newUpdateType.updateTypeId) ?? null);
    }, {
        onSuccess: (createdType, label) => {
            if (createdType == null) {
                errorNotification(lang, new Error(`Created update type "${label}" was not found`), 'create', lang.errorEntities.updateType);
            }
            else {
                storeRecent(RECENTS_STORAGE_KEY, createdType.updateTypeId);
                if (props.multi === true) {
                    props.onChange([...props.value, createdType.updateTypeId]);
                }
                else {
                    props.onChange(createdType.updateTypeId, createdType);
                }
            }
        },
        onError: (err) => errorNotification(lang, err, 'create', lang.errorEntities.updateType),
    });
    const label = useCallback((updateType) => lang.updateTypes[updateType.updateTypeId] || updateType.label, [lang]);
    // Both the translated and the raw label match a search
    const searchText = useCallback((updateType) => `${lang.updateTypes[updateType.updateTypeId] || ''} ${updateType.label}`, [lang]);
    const facet = useMemo(() => ({
        of: kindOf,
        options: [
            {
                key: 'task',
                label: lang.updateTypesSettings.filterAutomatic,
            },
            { key: 'pause', label: lang.updateTypesSettings.filterPause },
            { key: 'info', label: lang.updateTypesSettings.filterInfo },
        ].map((option) => ({ ...option, icon: KIND_ICON[option.key].icon })),
    }), [lang]);
    // Hovering a task shows its steps
    const rowAttributes = (updateType) => {
        const steps = updateType.procedureEnabled
            ? (updateType.procedure ?? [])
            : [];
        if (steps.length === 0) {
            return {};
        }
        return {
            'data-tooltip-id': 'tooltip-default',
            'data-tooltip-html': steps
                .map((todo, step) => `${step + 1}. ${lang.workflowTodoTypes[todo.type] || todo.type}`)
                .join('<br/>'),
        };
    };
    // Fuzzy search rarely comes up empty, so the create option is a fixed last
    // row of the results rather than an empty-state fallback
    const createRow = (search) => isAdmin === true && search !== ''
        ? {
            icon: React.createElement(PlusCircle, null),
            label: lang.updateTypesSettings.createNewUpdateType,
            description: `“${search}”`,
            onSelect: () => {
                if (!createMutation.isLoading) {
                    createMutation.mutate(search);
                }
            },
        }
        : null;
    return (React.createElement(SearchableSelect, { ...props, placeholder: props.placeholder ?? lang.updatesEditor.selectUpdateType, disabled: props.disabled === true || createMutation.isLoading, busy: createMutation.isLoading, items: updateTypes, getId: updateTypeId, getLabel: label, getSearchText: searchText, getIcon: kindIcon, getDescription: updateTypeDescription, getRowAttributes: rowAttributes, facet: facet, extraRow: createRow, labels: {
            search: lang.workflows.searchUpdates,
            all: lang.updateTypesSettings.allUpdateTypes,
        }, recentsStorageKey: RECENTS_STORAGE_KEY }));
};
export default UpdateTypeSelect;
//# sourceMappingURL=UpdateTypeSelect.js.map